import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getLeads, deleteLead, importLeads, checkLeadDomains, getLeadSegmentations, getLeadSourceDetails,
  getIndustryGroups,
} from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import AzotteGrid from '../components/AzotteGrid';
import {
  Box, Button, Typography, Paper, Stack, Chip, TextField, MenuItem,
  Alert, DialogActions, Grid, InputAdornment, Divider, IconButton, Tooltip,
  LinearProgress, FormControlLabel, Checkbox, Collapse, useMediaQuery, useTheme,
  FormControl, InputLabel, Select, ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  TrendingUp as TrendingUpIcon,
  NewReleases as NewIcon,
  Verified as QualifiedIcon,
  Handshake as PartnerIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
  OpenInNew as OpenInNewIcon,
  People as PeopleIcon,
  Email as EmailIcon,
  LinkedIn as LinkedInIcon,
  HealthAndSafety as HealthIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import {
  LEAD_STATUS_OPTIONS,
  LEAD_STATUS_COLORS as STATUS_COLORS_SHARED,
  INACTIVE_LEAD_STATUSES,
} from '../constants/leadStatus';

const STATUSES = LEAD_STATUS_OPTIONS.map(o => o.value);
const DEFAULT_STATUSES = STATUSES.filter(s => !INACTIVE_LEAD_STATUSES.includes(s));

const arraysEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
};
const SOURCES = ['ManualEntry', 'CsvImport', 'Google', 'LinkedIn', 'CrunchBase', 'Owler', 'Referral', 'Other', 'AiSearch'];
const ICP_FITS = [
  { value: 'CurrentFit', label: 'Current Fit' },
  { value: 'FuturePotential', label: 'Future Potential' },
  { value: 'NotAFit', label: 'Not a Fit' },
  { value: 'None', label: 'Not verdicted' },
];

const JSON_TARGET_FIELDS = [
  { key: 'companyName', label: 'Company Name *', required: true, aliases: ['companyname', 'business_name', 'businessname', 'company_name', 'company', 'name'] },
  { key: 'website', label: 'Website', aliases: ['website', 'domain', 'url', 'site', 'web'] },
  { key: 'linkedInUrl', label: 'LinkedIn URL', aliases: ['linkedin', 'linkedinurl', 'linkedin_url', 'linkedinprofile'] },
  { key: 'description', label: 'Description', aliases: ['description', 'business_description', 'long_business_description', 'about', 'summary'] },
  { key: 'industryName', label: 'Industry', aliases: ['industry', 'industryname', 'industry_name', 'sector'] },
  { key: 'companyType', label: 'Company Type', aliases: ['companytype', 'company_type', 'type', 'company_status', 'companystatus'] },
  { key: 'headcount', label: 'Headcount', aliases: ['headcount', 'employees', 'employee_count', 'size', 'staff'] },
  { key: 'address', label: 'Address', aliases: ['address', 'street'] },
  { key: 'city', label: 'City', aliases: ['city', 'town', 'based_in', 'basedin', 'location'] },
  { key: 'state', label: 'State', aliases: ['state', 'region', 'province'] },
  { key: 'postalCode', label: 'Postal Code', aliases: ['postalcode', 'postal_code', 'zip', 'zipcode', 'zip_code'] },
  { key: 'country', label: 'Country', aliases: ['country', 'countryname', 'category'] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'telephone', 'mobile', 'tel'] },
  { key: 'email', label: 'Email', aliases: ['email', 'e_mail', 'mail', 'contact_email'] },
  { key: 'tags', label: 'Tags', aliases: ['tags', 'tag', 'keywords'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'comment', 'comments'] },
];

const normalizeKey = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const autoDetectMapping = (jsonFields) => {
  const mapping = {};
  const normalizedFields = jsonFields.map(f => ({ raw: f, norm: normalizeKey(f) }));
  for (const target of JSON_TARGET_FIELDS) {
    const targetNorm = normalizeKey(target.key);
    const aliasNorms = [targetNorm, ...target.aliases.map(normalizeKey)];
    const match = normalizedFields.find(f => aliasNorms.includes(f.norm));
    if (match) mapping[target.key] = match.raw;
  }
  return mapping;
};

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Date added' },
  { value: 'updatedAt', label: 'Last updated' },
  { value: 'companyName', label: 'Company name' },
  { value: 'status', label: 'Status' },
  { value: 'azotteFitScore', label: 'Fit score' },
  { value: 'icpMatchPercentage', label: 'ICP match' },
  { value: 'country', label: 'Country' },
];

const ICP_FIT_CHIP = {
  CurrentFit:      { label: 'Current',  bg: '#dcfce7', text: '#166534', border: '#bbf7d0' },
  FuturePotential: { label: 'Future',   bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
  NotAFit:         { label: 'Not Fit',  bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
};

const STATUS_COLORS = STATUS_COLORS_SHARED;

export default function DiscoverPage() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [error, setError] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [leads, setLeads] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, new: 0, qualified: 0, partner: 0 });
  const [segmentations, setSegmentations] = useState([]);
  const [sourceDetails, setSourceDetails] = useState([]);
  const [industryGroups, setIndustryGroups] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: DEFAULT_STATUSES, segmentationId: '', source: '', sourceDetail: '', aiIcpFit: '', industryGroupId: '' });
  const [statusDraft, setStatusDraft] = useState(DEFAULT_STATUSES);
  const [statusOpen, setStatusOpen] = useState(false);
  const openStatus = () => { setStatusDraft(filters.status); setStatusOpen(true); };
  const closeStatus = () => { setStatusOpen(false); setFilters(f => ({ ...f, status: statusDraft })); };
  const toggleStatusDraft = (s) => {
    setStatusDraft(d => d.indexOf(s) > -1 ? d.filter(x => x !== s) : [...d, s]);
  };
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showArchived, setShowArchived] = useState(false);
  const [showDisqualified, setShowDisqualified] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [sourceName, setSourceName] = useState('');
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [jsonModal, setJsonModal] = useState(false);
  const [jsonStep, setJsonStep] = useState('input'); // 'input' | 'mapping'
  const [jsonText, setJsonText] = useState('');
  const [jsonFileName, setJsonFileName] = useState('');
  const [jsonRecords, setJsonRecords] = useState([]);
  const [jsonFields, setJsonFields] = useState([]);
  const [jsonMapping, setJsonMapping] = useState({});
  const [jsonSourceName, setJsonSourceName] = useState('');
  const [jsonError, setJsonError] = useState('');
  const jsonFileInputRef = useRef(null);
  const [checkingDomains, setCheckingDomains] = useState(false);
  const [domainCheckMsg, setDomainCheckMsg] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchLeads = useCallback(async () => {
    try {
      const params = { page, pageSize: rowsPerPage };
      if (filters.search) params.search = filters.search;
      // Send multi-select as comma-separated `statuses`. Backend filters at SQL level.
      // Empty array = no filter (returns all). Full set also passes everything but explicitly.
      if (Array.isArray(filters.status) && filters.status.length > 0)
        params.statuses = filters.status.join(',');
      if (filters.segmentationId) params.segmentationId = filters.segmentationId;
      if (filters.source) params.source = filters.source;
      if (filters.sourceDetail) params.sourceDetail = filters.sourceDetail;
      if (filters.aiIcpFit) params.aiIcpFit = filters.aiIcpFit;
      if (filters.industryGroupId) params.industryGroupId = filters.industryGroupId;
      if (sortBy) params.sortBy = sortBy;
      if (sortOrder) params.sortOrder = sortOrder;
      if (showArchived) params.includeArchived = true;
      if (showDisqualified) params.includeDisqualified = true;
      const { data } = await getLeads(params);
      const items = data?.items || [];
      setLeads(items);
      setTotalCount(data?.total || 0);
      setStats({
        total: data?.stats?.total || 0,
        new: data?.stats?.new || 0,
        qualified: data?.stats?.qualified || 0,
        partner: data?.stats?.partner || 0,
      });
      try { sessionStorage.setItem('leadsListIds', JSON.stringify(items.map(l => l.id))); } catch { /* ignore */ }
    } catch { setError('Failed to load leads'); }
  }, [filters, sortBy, sortOrder, showArchived, showDisqualified, page, rowsPerPage]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);
  useEffect(() => { setPage(0); }, [filters, sortBy, sortOrder, showArchived, showDisqualified]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getLeadSegmentations();
        setSegmentations(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        const { data } = await getLeadSourceDetails();
        setSourceDetails(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
    (async () => {
      try {
        const { data } = await getIndustryGroups();
        setIndustryGroups(Array.isArray(data) ? data : []);
      } catch { /* ignore */ }
    })();
  }, []);

  // Status counts as "filtered" only when it diverges from the default set.
  const statusFiltered = !arraysEqual(filters.status, DEFAULT_STATUSES);
  const activeFilterCount = [statusFiltered, filters.segmentationId, filters.source, filters.sourceDetail, filters.aiIcpFit, filters.industryGroupId].filter(Boolean).length;

  const clearFilters = () => setFilters({ search: '', status: DEFAULT_STATUSES, segmentationId: '', source: '', sourceDetail: '', aiIcpFit: '', industryGroupId: '' });

  const handleDelete = async (id) => {
    if (!(await dialog.confirm({ title: 'Delete lead', message: 'Delete this lead?', confirmText: 'Delete', danger: true }))) return;
    try {
      await deleteLead(id);
      fetchLeads();
    } catch { setError('Failed to delete lead'); }
  };

  const handleCheckDomains = async () => {
    if (!(await dialog.confirm({ title: 'Run domain health check', message: 'Run domain health check on unchecked CSV-imported leads? Broken domains will be marked Disqualified.', confirmText: 'Run check' }))) return;
    setCheckingDomains(true);
    setDomainCheckMsg('');
    try {
      const { data } = await checkLeadDomains();
      setDomainCheckMsg(
        `Domain check done: considered ${data.considered ?? data.Considered}, checked ${data.checked ?? data.Checked}, disqualified ${data.disqualified ?? data.Disqualified}, errors ${data.errors ?? data.Errors}.`
      );
      fetchLeads();
    } catch (e) {
      setError(e?.response?.data?.message || 'Domain check failed');
    } finally {
      setCheckingDomains(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim()); current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseHeadcount = (raw) => {
    if (!raw) return undefined;
    const m = String(raw).replace(/[,\s]/g, '').match(/\d+/);
    return m ? parseInt(m[0], 10) : undefined;
  };

  const buildLeadsFromCsv = (text, sourceOverride) => {
    const lines = text.replace(/\r\n?/g, '\n').split('\n').filter(l => l.trim());
    if (lines.length < 2) return { leads: [], error: 'CSV must have a header row and at least one data row' };
    const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());
    const override = (sourceOverride || '').trim();
    const leads = lines.slice(1).map(line => {
      const values = parseCsvLine(line);
      const get = (...keys) => {
        for (const k of keys) {
          const idx = headers.indexOf(k);
          if (idx >= 0) {
            const v = (values[idx] || '').trim();
            if (v) return v;
          }
        }
        return '';
      };
      const companyName = get('company name', 'companyname', 'company');
      if (!companyName) return null;
      return {
        companyName,
        website: get('domain', 'website') || undefined,
        linkedInUrl: get('linkedin', 'linkedin url', 'linkedinurl') || undefined,
        industryName: get('industry') || undefined,
        companyType: get('company type', 'companytype', 'type') || undefined,
        headcount: parseHeadcount(get('headcount', 'employees', 'employee count', 'size')),
        description: get('description', 'about') || undefined,
        city: get('city') || undefined,
        state: get('state', 'region', 'province') || undefined,
        postalCode: get('postal code', 'postalcode', 'zip', 'zip code') || undefined,
        country: get('country') || undefined,
        tags: get('tags') || undefined,
        source: 'CsvImport',
        sourceDetail: override || get('source') || undefined,
      };
    }).filter(Boolean);
    return { leads };
  };

  const handleImport = async (e) => {
    e.preventDefault();
    setError('');
    const { leads: leadsList, error: parseErr } = buildLeadsFromCsv(csvText, sourceName);
    if (parseErr) { setError(parseErr); return; }
    if (leadsList.length === 0) { setError('No rows with a Company Name found'); return; }

    const CHUNK = 100;
    const totals = { processed: 0, total: leadsList.length, imported: 0, duplicates: 0, failed: 0, errors: [] };
    setImporting(true);
    setImportProgress({ ...totals });

    try {
      for (let i = 0; i < leadsList.length; i += CHUNK) {
        const chunk = leadsList.slice(i, i + CHUNK);
        try {
          const { data } = await importLeads({ leads: chunk });
          totals.imported += data.imported || 0;
          totals.duplicates += data.duplicates || 0;
          totals.failed += data.failed || 0;
          if (Array.isArray(data.errors)) totals.errors.push(...data.errors);
        } catch (err) {
          totals.failed += chunk.length;
          totals.errors.push(err.response?.data?.message || `Batch ${Math.floor(i / CHUNK) + 1} failed`);
        }
        totals.processed = Math.min(i + chunk.length, leadsList.length);
        setImportProgress({ ...totals });
      }
      fetchLeads();
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setImportModal(false);
    setCsvText('');
    setCsvFileName('');
    setSourceName('');
    setImportProgress(null);
  };

  const handleJsonFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setJsonFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setJsonText(ev.target.result);
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const collectJsonFields = (records) => {
    const set = new Set();
    for (const r of records) {
      if (r && typeof r === 'object' && !Array.isArray(r)) {
        Object.keys(r).forEach(k => set.add(k));
      }
    }
    return Array.from(set);
  };

  const handleJsonParse = () => {
    setJsonError('');
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setJsonError('Invalid JSON: ' + (err?.message || 'parse error'));
      return;
    }
    const records = Array.isArray(parsed) ? parsed : [parsed];
    const objectRecords = records.filter(r => r && typeof r === 'object' && !Array.isArray(r));
    if (objectRecords.length === 0) {
      setJsonError('JSON must be an object or an array of objects');
      return;
    }
    const fields = collectJsonFields(objectRecords);
    if (fields.length === 0) {
      setJsonError('No fields detected in the JSON records');
      return;
    }
    setJsonRecords(objectRecords);
    setJsonFields(fields);
    setJsonMapping(autoDetectMapping(fields));
    setJsonStep('mapping');
  };

  const buildLeadsFromJson = (records, mapping, sourceOverride) => {
    const get = (rec, targetKey) => {
      const src = mapping[targetKey];
      if (!src) return undefined;
      const v = rec[src];
      if (v === null || v === undefined) return undefined;
      const s = typeof v === 'string' ? v.trim() : String(v).trim();
      return s ? s : undefined;
    };
    return records.map(rec => {
      const companyName = get(rec, 'companyName');
      if (!companyName) return null;
      return {
        companyName,
        website: get(rec, 'website'),
        linkedInUrl: get(rec, 'linkedInUrl'),
        industryName: get(rec, 'industryName'),
        companyType: get(rec, 'companyType'),
        headcount: parseHeadcount(get(rec, 'headcount')),
        description: get(rec, 'description'),
        address: get(rec, 'address'),
        city: get(rec, 'city'),
        state: get(rec, 'state'),
        postalCode: get(rec, 'postalCode'),
        country: get(rec, 'country'),
        phone: get(rec, 'phone'),
        email: get(rec, 'email'),
        tags: get(rec, 'tags'),
        notes: get(rec, 'notes'),
        source: 'Other',
        sourceDetail: (sourceOverride || '').trim() || undefined,
      };
    }).filter(Boolean);
  };

  const handleJsonImport = async () => {
    setJsonError('');
    if (!jsonMapping.companyName) {
      setJsonError('Map "Company Name" before importing');
      return;
    }
    const leadsList = buildLeadsFromJson(jsonRecords, jsonMapping, jsonSourceName);
    if (leadsList.length === 0) {
      setJsonError('No records had a Company Name value');
      return;
    }
    const CHUNK = 100;
    const totals = { processed: 0, total: leadsList.length, imported: 0, duplicates: 0, failed: 0, errors: [] };
    setImporting(true);
    setImportProgress({ ...totals });
    try {
      for (let i = 0; i < leadsList.length; i += CHUNK) {
        const chunk = leadsList.slice(i, i + CHUNK);
        try {
          const { data } = await importLeads({ leads: chunk });
          totals.imported += data.imported || 0;
          totals.duplicates += data.duplicates || 0;
          totals.failed += data.failed || 0;
          if (Array.isArray(data.errors)) totals.errors.push(...data.errors);
        } catch (err) {
          totals.failed += chunk.length;
          totals.errors.push(err.response?.data?.message || `Batch ${Math.floor(i / CHUNK) + 1} failed`);
        }
        totals.processed = Math.min(i + chunk.length, leadsList.length);
        setImportProgress({ ...totals });
      }
      fetchLeads();
    } finally {
      setImporting(false);
    }
  };

  const closeJsonModal = () => {
    setJsonModal(false);
    setJsonStep('input');
    setJsonText('');
    setJsonFileName('');
    setJsonRecords([]);
    setJsonFields([]);
    setJsonMapping({});
    setJsonSourceName('');
    setJsonError('');
    setImportProgress(null);
  };

  const confidenceColor = (score) => {
    if (score >= 80) return '#16a34a';
    if (score >= 60) return '#d97706';
    if (score >= 40) return '#ea580c';
    return '#dc2626';
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const statCards = [
    { label: 'Total Leads', value: stats.total, icon: <TrendingUpIcon />, color: '#4f46e5', bg: '#eef2ff' },
    { label: 'New', value: stats.new, icon: <NewIcon />, color: '#0284c7', bg: '#f0f9ff' },
    { label: 'Qualified', value: stats.qualified, icon: <QualifiedIcon />, color: '#16a34a', bg: '#f0fdf4' },
    { label: 'Partner Qualified', value: stats.partner, icon: <PartnerIcon />, color: '#7c3aed', bg: '#f5f3ff' },
  ];

  const leadColumns = [
    { key: 'id', header: 'ID', hide: false, mobileColSpan: 4, render: (val) => <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{val}</Typography> },
    {
      key: 'companyName', header: 'Company', mobileColSpan: 8,
      render: (val) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BusinessIcon sx={{ fontSize: 14, color: '#64748b' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{val}</Typography>
        </Box>
      ),
    },
    { key: 'industryName', header: 'Industry', mobileFullWidth: true, render: (val) => val ? <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>{val}</Typography> : <Typography sx={{ color: '#cbd5e1', fontSize: '0.8125rem' }}>—</Typography> },
    { key: 'country', header: 'Country', mobileColSpan: 6, render: (val) => val ? <Typography sx={{ fontSize: '0.8125rem' }}>{val}</Typography> : <Typography sx={{ color: '#cbd5e1', fontSize: '0.8125rem' }}>—</Typography> },
    {
      key: 'createdAt', header: 'Added', mobileColSpan: 6,
      render: (val) => <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>{formatDate(val)}</Typography>,
    },
    {
      key: 'status', header: 'Status', mobileColSpan: 6,
      render: (val) => {
        const c = STATUS_COLORS[val] || { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' };
        return <Chip label={val} size="small" sx={{ bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600, fontSize: '0.6875rem', height: 22 }} />;
      },
    },
    {
      key: 'azotteFitScore', header: 'Fit Score', mobileColSpan: 6,
      render: (val) => val != null ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 32, height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
            <Box sx={{ width: `${val}%`, height: '100%', bgcolor: confidenceColor(val), borderRadius: 2 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', color: confidenceColor(val) }}>{val}</Typography>
        </Box>
      ) : <Typography sx={{ color: '#cbd5e1', fontSize: '0.8125rem' }}>—</Typography>,
    },
    {
      key: 'aiIcpFit', header: 'ICP Fit', mobileColSpan: 6,
      render: (val) => {
        if (!val) return <Typography sx={{ color: '#cbd5e1', fontSize: '0.8125rem' }}>—</Typography>;
        const c = ICP_FIT_CHIP[val] || { label: val, bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' };
        return <Chip label={c.label} size="small" sx={{ bgcolor: c.bg, color: c.text, border: `1px solid ${c.border}`, fontWeight: 600, fontSize: '0.6875rem', height: 22 }} />;
      },
    },
    {
      key: 'contactCount', header: 'Contacts', mobileColSpan: 6,
      render: (val, row) => {
        const total = val || 0;
        const emails = row.contactEmailCount || 0;
        const li = row.contactLinkedInCount || 0;
        if (total === 0) return <Typography sx={{ color: '#cbd5e1', fontSize: '0.8125rem' }}>—</Typography>;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
              <PeopleIcon sx={{ fontSize: 14, color: '#64748b' }} />
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#0f172a' }}>{total}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>(</Typography>
            <Tooltip title={`${li} with LinkedIn`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <LinkedInIcon sx={{ fontSize: 13, color: li > 0 ? '#0a66c2' : '#cbd5e1' }} />
                <Typography sx={{ fontSize: '0.75rem', color: li > 0 ? '#475569' : '#cbd5e1', fontWeight: 500 }}>{li}</Typography>
              </Box>
            </Tooltip>
            <Tooltip title={`${emails} with email`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <EmailIcon sx={{ fontSize: 13, color: emails > 0 ? '#dc2626' : '#cbd5e1' }} />
                <Typography sx={{ fontSize: '0.75rem', color: emails > 0 ? '#475569' : '#cbd5e1', fontWeight: 500 }}>{emails}</Typography>
              </Box>
            </Tooltip>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>)</Typography>
          </Box>
        );
      },
    },
    { key: 'source', header: 'Source', hideOnMobile: true, render: (val) => <Chip label={val} size="small" variant="outlined" sx={{ fontSize: '0.6875rem', height: 22 }} /> },
    {
      key: 'actions', header: '', width: 80,
      render: (_, row) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Open lead">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/leads/${row.id}`); }} sx={{ color: '#4f46e5', opacity: 0.7, '&:hover': { opacity: 1, bgcolor: '#eef2ff' } }}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete lead">
            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      {/* ── Page Header ── */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        gap: { xs: 1.5, sm: 2 },
        mb: 3,
      }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Leads</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
            Find and qualify new leads for your pipeline
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            '& > *': { flex: { xs: '1 1 0', sm: '0 0 auto' } },
          }}
        >
          <Tooltip title="Probe homepages of unchecked CSV-imported leads. Disqualifies broken / parked / expired domains.">
            <span style={{ display: 'flex', flex: isMobile ? '1 1 0' : '0 0 auto' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HealthIcon />}
                onClick={handleCheckDomains}
                disabled={checkingDomains}
                fullWidth={isMobile}
                sx={{ borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
              >
                {checkingDomains ? 'Checking…' : (isMobile ? 'Check' : 'Check Domains')}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setImportModal(true)}
            sx={{ borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
          >
            {isMobile ? 'Import' : 'Import CSV'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadIcon />}
            onClick={() => setJsonModal(true)}
            sx={{ borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
          >
            {isMobile ? 'JSON' : 'Import JSON'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate('/leads/new')}
            sx={{ boxShadow: '0 1px 3px rgba(79,70,229,0.3)' }}
          >
            {isMobile ? 'Add' : 'Add Lead'}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {domainCheckMsg && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setDomainCheckMsg('')}>{domainCheckMsg}</Alert>}
      {checkingDomains && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* ── Stat Cards ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(5, 1fr)',
        },
        gap: 2,
        mb: 3,
      }}>
        {statCards.map(s => (
          <Paper
            key={s.label}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
              transition: 'box-shadow 0.15s',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
            }}
          >
            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.color, flexShrink: 0 }}>
              {s.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2, color: s.color }}>{s.value}</Typography>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Filters ── */}
      <Paper
        elevation={0}
        sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #f1f5f9' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search company, website, contact name, email, phone…"
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            sx={{ flex: '1 1 200px', minWidth: 160 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: '#94a3b8' }} /></InputAdornment>,
              endAdornment: filters.search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilters({ ...filters, search: '' })} edge="end">
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {isMobile && (
            <Button
              size="small"
              variant={filtersOpen || activeFilterCount > 0 ? 'contained' : 'outlined'}
              onClick={() => setFiltersOpen(o => !o)}
              startIcon={<FilterListIcon />}
              endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                flexShrink: 0,
                borderColor: '#e2e8f0',
                color: filtersOpen || activeFilterCount > 0 ? undefined : '#475569',
              }}
            >
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </Button>
          )}

          {!isMobile && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <FormControl size="small" sx={{ width: 180 }}>
                <InputLabel id="status-multi-label">Status</InputLabel>
                <Select
                  labelId="status-multi-label"
                  label="Status"
                  multiple
                  open={statusOpen}
                  onOpen={openStatus}
                  onClose={closeStatus}
                  value={statusDraft}
                  renderValue={() => {
                    const selected = statusOpen ? statusDraft : filters.status;
                    if (!selected || selected.length === 0) return 'None';
                    if (arraysEqual(selected, STATUSES)) return 'All';
                    if (arraysEqual(selected, DEFAULT_STATUSES)) return 'Default';
                    if (selected.length <= 2) return selected.join(', ');
                    return `${selected.length} selected`;
                  }}
                >
                  {STATUSES.map(s => (
                    <MenuItem key={s} onClick={() => toggleStatusDraft(s)} dense>
                      <Checkbox size="small" checked={statusDraft.indexOf(s) > -1} />
                      <ListItemText primary={s} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                select size="small" label="Segmentation"
                value={filters.segmentationId}
                onChange={e => setFilters({ ...filters, segmentationId: e.target.value })}
                sx={{ width: 180 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">All Segmentations</MenuItem>
                {segmentations.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Source"
                value={filters.source}
                onChange={e => setFilters({ ...filters, source: e.target.value })}
                sx={{ width: 140 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">All Sources</MenuItem>
                {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Source Name"
                value={filters.sourceDetail}
                onChange={e => setFilters({ ...filters, sourceDetail: e.target.value })}
                sx={{ width: 180 }}
                SelectProps={{ displayEmpty: true }}
                disabled={sourceDetails.length === 0}
              >
                <MenuItem value="">All Source Names</MenuItem>
                {sourceDetails.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="ICP Fit"
                value={filters.aiIcpFit}
                onChange={e => setFilters({ ...filters, aiIcpFit: e.target.value })}
                sx={{ width: 160 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">All ICP Fits</MenuItem>
                {ICP_FITS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Industry Group"
                value={filters.industryGroupId}
                onChange={e => setFilters({ ...filters, industryGroupId: e.target.value })}
                sx={{ width: 180 }}
                SelectProps={{ displayEmpty: true }}
                disabled={industryGroups.length === 0}
              >
                <MenuItem value="">All Industry Groups</MenuItem>
                {industryGroups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Sort by"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                sx={{ width: 160 }}
              >
                {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Order"
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
                sx={{ width: 110 }}
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </TextField>

              <FormControlLabel
                control={<Checkbox size="small" checked={showArchived}
                  onChange={e => setShowArchived(e.target.checked)} />}
                label={<Typography variant="caption">Show archived</Typography>}
                sx={{ ml: 0 }}
              />

              {activeFilterCount > 0 && (
                <Tooltip title="Clear all filters">
                  <Chip
                    label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
                    size="small"
                    onDelete={clearFilters}
                    color="primary"
                    variant="outlined"
                    icon={<FilterListIcon />}
                    sx={{ ml: 'auto', cursor: 'pointer', fontWeight: 600 }}
                  />
                </Tooltip>
              )}
            </>
          )}
        </Box>

        {isMobile && (
          <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="status-multi-label-mobile">Status</InputLabel>
                <Select
                  labelId="status-multi-label-mobile"
                  label="Status"
                  multiple
                  open={statusOpen}
                  onOpen={openStatus}
                  onClose={closeStatus}
                  value={statusDraft}
                  renderValue={() => {
                    const selected = statusOpen ? statusDraft : filters.status;
                    if (!selected || selected.length === 0) return 'None';
                    if (arraysEqual(selected, STATUSES)) return 'All';
                    if (arraysEqual(selected, DEFAULT_STATUSES)) return 'Default';
                    if (selected.length <= 2) return selected.join(', ');
                    return `${selected.length} selected`;
                  }}
                >
                  {STATUSES.map(s => (
                    <MenuItem key={s} onClick={() => toggleStatusDraft(s)} dense>
                      <Checkbox size="small" checked={statusDraft.indexOf(s) > -1} />
                      <ListItemText primary={s} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                select size="small" label="Segmentation"
                value={filters.segmentationId}
                onChange={e => setFilters({ ...filters, segmentationId: e.target.value })}
                SelectProps={{ displayEmpty: true }}
                fullWidth
              >
                <MenuItem value="">All Segmentations</MenuItem>
                {segmentations.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Source"
                value={filters.source}
                onChange={e => setFilters({ ...filters, source: e.target.value })}
                SelectProps={{ displayEmpty: true }}
                fullWidth
              >
                <MenuItem value="">All Sources</MenuItem>
                {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Source Name"
                value={filters.sourceDetail}
                onChange={e => setFilters({ ...filters, sourceDetail: e.target.value })}
                SelectProps={{ displayEmpty: true }}
                fullWidth
                disabled={sourceDetails.length === 0}
              >
                <MenuItem value="">All Source Names</MenuItem>
                {sourceDetails.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="ICP Fit"
                value={filters.aiIcpFit}
                onChange={e => setFilters({ ...filters, aiIcpFit: e.target.value })}
                SelectProps={{ displayEmpty: true }}
                fullWidth
              >
                <MenuItem value="">All ICP Fits</MenuItem>
                {ICP_FITS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Industry Group"
                value={filters.industryGroupId}
                onChange={e => setFilters({ ...filters, industryGroupId: e.target.value })}
                SelectProps={{ displayEmpty: true }}
                fullWidth
                disabled={industryGroups.length === 0}
              >
                <MenuItem value="">All Industry Groups</MenuItem>
                {industryGroups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Sort by"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                fullWidth
              >
                {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>

              <TextField
                select size="small" label="Order"
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
                fullWidth
              >
                <MenuItem value="desc">Desc</MenuItem>
                <MenuItem value="asc">Asc</MenuItem>
              </TextField>

              <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={showArchived}
                    onChange={e => setShowArchived(e.target.checked)} />}
                  label={<Typography variant="caption">Show archived</Typography>}
                  sx={{ ml: 0 }}
                />
                {activeFilterCount > 0 && (
                  <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ color: '#475569' }}>
                    Clear ({activeFilterCount})
                  </Button>
                )}
              </Box>
            </Box>
          </Collapse>
        )}
      </Paper>

      {/* ── Results count ── */}
      {totalCount > 0 && (
        <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8', mb: 1.5 }}>
          Showing {leads.length} of {totalCount} lead{totalCount !== 1 ? 's' : ''}
          {activeFilterCount > 0 && ' (filtered)'}
        </Typography>
      )}

      {/* ── Table / Empty state ── */}
      {totalCount === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: 6, textAlign: 'center', borderRadius: 2, border: '1px dashed #e2e8f0' }}
        >
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <BusinessIcon sx={{ fontSize: 28, color: '#cbd5e1' }} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: '#475569', mb: 0.5 }}>No leads found</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: '#94a3b8', mb: 3 }}>
            {activeFilterCount > 0
              ? 'No leads match your current filters. Try adjusting or clearing them.'
              : 'Start building your pipeline by adding your first lead.'}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            {activeFilterCount > 0 ? (
              <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>Clear Filters</Button>
            ) : (
              <>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/leads/new')}>Add Lead</Button>
                <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setImportModal(true)}>Import CSV</Button>
              </>
            )}
          </Stack>
        </Paper>
      ) : (
        <AzotteGrid
          columns={leadColumns}
          rows={leads}
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          handleRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          onDoubleClick={(row) => navigate(`/leads/${row.id}`)}
          actionColumnKey="actions"
        />
      )}

      {/* ── Import CSV Modal ── */}
      {importModal && (
        <Modal
          title="Import Leads from CSV"
          onClose={() => { if (!importing) closeImportModal(); }}
        >
          {!importProgress && (
            <form onSubmit={handleImport}>
              <TextField
                fullWidth
                label="Source name"
                placeholder="e.g. Apollo Export Q2 2026"
                value={sourceName}
                onChange={e => setSourceName(e.target.value)}
                helperText="Optional. Applied to every imported lead. Overrides any 'Source' column in the CSV."
                sx={{ mb: 1.5 }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <Button
                variant="outlined"
                fullWidth
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current.click()}
                sx={{ mb: 1.5, py: 1.5, borderStyle: 'dashed', borderColor: '#cbd5e1', color: '#64748b', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
              >
                {csvFileName ? csvFileName : 'Choose CSV file…'}
              </Button>
              <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="caption" sx={{ mx: 1.5, color: 'text.disabled' }}>or paste below</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Stack>
              <TextField
                fullWidth multiline rows={8} label="CSV Data"
                value={csvText}
                onChange={e => { setCsvText(e.target.value); setCsvFileName(''); }}
                sx={{ mb: 1, '& textarea': { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                placeholder={'Company Name,Domain,Country\nAcme Corp,acme.com,France'}
              />
              <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="caption">
                  Recognized columns (any order, case-insensitive): Company Name, Domain, Industry, City, State, Postal Code, Country, Headcount, Linkedin, Description, Company Type, Tags, Source.
                </Typography>
              </Alert>
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button variant="text" color="inherit" onClick={closeImportModal}>Cancel</Button>
                <Button variant="contained" type="submit" disabled={!csvText.trim()}>Import Leads</Button>
              </DialogActions>
            </form>
          )}

          {importProgress && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {importing ? 'Importing…' : 'Import complete'}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>
                  {importProgress.processed} / {importProgress.total} rows
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={importProgress.total ? (importProgress.processed / importProgress.total) * 100 : 0}
                sx={{
                  height: 10, borderRadius: 5, bgcolor: '#f1f5f9',
                  '& .MuiLinearProgress-bar': { bgcolor: importing ? '#4f46e5' : '#16a34a' },
                  mb: 2,
                }}
              />
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid size={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#16a34a' }}>{importProgress.imported}</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Imported</Typography>
                  </Paper>
                </Grid>
                <Grid size={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#d97706' }}>{importProgress.duplicates}</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Duplicates</Typography>
                  </Paper>
                </Grid>
                <Grid size={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{importProgress.failed}</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Failed</Typography>
                  </Paper>
                </Grid>
              </Grid>
              {!importing && importProgress.errors && importProgress.errors.length > 0 && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    {importProgress.errors.length} error{importProgress.errors.length > 1 ? 's' : ''}:
                  </Typography>
                  <Box sx={{ maxHeight: 140, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {importProgress.errors.slice(0, 50).map((er, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', color: '#991b1b' }}>• {er}</Typography>
                    ))}
                    {importProgress.errors.length > 50 && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
                        …and {importProgress.errors.length - 50} more
                      </Typography>
                    )}
                  </Box>
                </Alert>
              )}
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button
                  variant="contained"
                  onClick={closeImportModal}
                  disabled={importing}
                >
                  {importing ? 'Please wait…' : 'Done'}
                </Button>
              </DialogActions>
            </Box>
          )}
        </Modal>
      )}

      {/* ── Import JSON Modal ── */}
      {jsonModal && (
        <Modal
          title="Import Leads from JSON"
          maxWidth="md"
          onClose={() => { if (!importing) closeJsonModal(); }}
        >
          {jsonStep === 'input' && !importProgress && (
            <Box>
              <input
                ref={jsonFileInputRef}
                type="file"
                accept=".json,application/json"
                style={{ display: 'none' }}
                onChange={handleJsonFileSelect}
              />
              <Button
                variant="outlined"
                fullWidth
                startIcon={<UploadIcon />}
                onClick={() => jsonFileInputRef.current.click()}
                sx={{ mb: 1.5, py: 1.5, borderStyle: 'dashed', borderColor: '#cbd5e1', color: '#64748b', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
              >
                {jsonFileName ? jsonFileName : 'Choose JSON file…'}
              </Button>
              <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="caption" sx={{ mx: 1.5, color: 'text.disabled' }}>or paste below</Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
              </Stack>
              <TextField
                fullWidth multiline rows={10} label="JSON Data"
                value={jsonText}
                onChange={e => { setJsonText(e.target.value); setJsonFileName(''); }}
                sx={{ mb: 1, '& textarea': { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                placeholder={'[\n  { "name": "Acme Corp", "website": "acme.com", "based_in": "Paris" }\n]'}
              />
              {jsonError && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{jsonError}</Alert>}
              <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="caption">
                  Provide an array of objects (or a single object). The next step lets you map JSON fields to lead columns.
                </Typography>
              </Alert>
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button variant="text" color="inherit" onClick={closeJsonModal}>Cancel</Button>
                <Button variant="contained" onClick={handleJsonParse} disabled={!jsonText.trim()}>Next: Map Fields</Button>
              </DialogActions>
            </Box>
          )}

          {jsonStep === 'mapping' && !importProgress && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1.5, color: '#64748b' }}>
                Detected <b>{jsonRecords.length}</b> record{jsonRecords.length === 1 ? '' : 's'} with <b>{jsonFields.length}</b> field{jsonFields.length === 1 ? '' : 's'}. Map JSON fields to your lead columns.
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Source name"
                placeholder="e.g. EU-Startups Directory March 2026"
                value={jsonSourceName}
                onChange={e => setJsonSourceName(e.target.value)}
                helperText="Optional. Stored as Source Detail on every imported lead."
                sx={{ mb: 2 }}
              />
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, bgcolor: '#f8fafc', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569' }}>Lead Column</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569' }}>JSON Field</Typography>
                </Box>
                <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
                  {JSON_TARGET_FIELDS.map((f, idx) => (
                    <Box
                      key={f.key}
                      sx={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'center',
                        px: 2, py: 1,
                        borderTop: idx === 0 ? 'none' : '1px solid', borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: f.required ? 600 : 400, color: f.required ? '#dc2626' : '#0f172a' }}>
                        {f.label}
                      </Typography>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={jsonMapping[f.key] || ''}
                        onChange={(e) => setJsonMapping(m => ({ ...m, [f.key]: e.target.value }))}
                      >
                        <MenuItem value=""><em>— skip —</em></MenuItem>
                        {jsonFields.map(jf => (
                          <MenuItem key={jf} value={jf}>{jf}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  ))}
                </Box>
              </Box>
              {jsonError && <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>{jsonError}</Alert>}
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button variant="text" color="inherit" onClick={() => setJsonStep('input')}>Back</Button>
                <Button variant="text" color="inherit" onClick={closeJsonModal}>Cancel</Button>
                <Button variant="contained" onClick={handleJsonImport} disabled={!jsonMapping.companyName}>
                  Import {jsonRecords.length} Lead{jsonRecords.length === 1 ? '' : 's'}
                </Button>
              </DialogActions>
            </Box>
          )}

          {importProgress && (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {importing ? 'Importing…' : 'Import complete'}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>
                  {importProgress.processed} / {importProgress.total} records
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={importProgress.total ? (importProgress.processed / importProgress.total) * 100 : 0}
                sx={{
                  height: 10, borderRadius: 5, bgcolor: '#f1f5f9',
                  '& .MuiLinearProgress-bar': { bgcolor: importing ? '#4f46e5' : '#16a34a' },
                  mb: 2,
                }}
              />
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid size={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#16a34a' }}>{importProgress.imported}</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Imported</Typography>
                  </Paper>
                </Grid>
                <Grid size={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#d97706' }}>{importProgress.duplicates}</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Duplicates</Typography>
                  </Paper>
                </Grid>
                <Grid size={4}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{importProgress.failed}</Typography>
                    <Typography sx={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>Failed</Typography>
                  </Paper>
                </Grid>
              </Grid>
              {!importing && importProgress.errors && importProgress.errors.length > 0 && (
                <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    {importProgress.errors.length} error{importProgress.errors.length > 1 ? 's' : ''}:
                  </Typography>
                  <Box sx={{ maxHeight: 140, overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {importProgress.errors.slice(0, 50).map((er, idx) => (
                      <Typography key={idx} variant="caption" sx={{ display: 'block', color: '#991b1b' }}>• {er}</Typography>
                    ))}
                    {importProgress.errors.length > 50 && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.5 }}>
                        …and {importProgress.errors.length - 50} more
                      </Typography>
                    )}
                  </Box>
                </Alert>
              )}
              <DialogActions sx={{ px: 0, pb: 0 }}>
                <Button variant="contained" onClick={closeJsonModal} disabled={importing}>
                  {importing ? 'Please wait…' : 'Done'}
                </Button>
              </DialogActions>
            </Box>
          )}
        </Modal>
      )}
    </Box>
  );
}
